import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSelect, useSwitch } from '@chilecompra/react-kit'
import { Card, DataTable, Typography, Select, Switch } from '@chilecompra/react-kit/components'
import { onGetAgilePurchaseProvidersThunk } from './AgilePurchaseProviders.actions'
import { exportToCsv } from '../../modules/utils/exports'
import { Content, GridContainer, GridItem, Link, Description, Root, Title } from './AgilePurchaseProviders.styles'
import { generateYears } from '../../modules/utils/years'
import { getMonth } from '../../modules/utils/months'
import DataLayout, { DataLayoutChild, DataLayoutViews, useDataLayout } from '../../components/Layouts/DataLayout'
import { formatAmount } from '../../modules/utils/formatters'
import { SORT_DIRECTIONS_DICTIONARY } from '@chilecompra/react-kit/components/Tables/DataTable.utils'
import { REGIONS_ALL } from '../../config/settings/constants'

/**
 * The AgilePurchaseProvider's container.
 *
 */
const AgilePurchaseProviders = () => {
  const dispatch = useDispatch()
  const { endYear, latestUpdate, loading, regions: regionOptions } = useSelector(state => state.topNavbar)
  const { data, csv } = useSelector(state => state.agilePurchaseProviders)

  const { value: currencyValue, onChange: handleChangeCurrency } = useSwitch()

  const yearsOptions = useMemo(() => generateYears(endYear), [endYear])

  const { value: regionValues, onChange: handleChangeRegion } = useSelect({
    initialValue: REGIONS_ALL,
    changeCallback: updatedRegion => {
      dispatch(onGetAgilePurchaseProvidersThunk({ year: yearValue, region: updatedRegion }))
    }
  })

  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: getMonth() === 0 ? endYear - 1 : endYear,
    changeCallback: updatedValue => {
      dispatch(onGetAgilePurchaseProvidersThunk({ year: updatedValue, region: regionValues }))
    },
    overwriteCallback: () => endYear
  })

  const {
    disableDownloadFile,
    ref: dataLayoutRef,
    onCaptureImage: handleCaptureImage,
    onChangeView: handleChangeView,
    onDownloadFile: handleDownloadFile
  } = useDataLayout({
    dependencies: [csv, yearValue],
    downloadFileCallback: () => exportToCsv(csv, `CompraAgil_Proveedores_${yearValue}`)
  })
  useEffect(() => {
    if (!yearValue) return
    dispatch(onGetAgilePurchaseProvidersThunk({ year: yearValue, region: regionValues }))
  }, [yearValue])

  return (
    <Root>
      <Content>
        <Typography
          color="primary"
          fontSize="18px"
          fontWeight="bold"
          lineHeight="21px"
          margin="0 0 24px"
          variant="body1"
        >
          Todos los proveedores en Compra Ágil
        </Typography>
        <GridContainer>
          <GridItem xs={6}>
            <Title>Proveedores que transan a través de Compra Ágil</Title>
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={5}>
            <Description>
              Revisa los montos y la cantidad de órdenes de compra realizadas mediante Compra Ágil de todos los
              proveedores del Estado. Puedes filtrar la información por región y por año.
            </Description>
          </GridItem>
        </GridContainer>

        <DataLayout
          disableCaptureImage
          disableChart
          disableDownloadFile={disableDownloadFile}
          header={
            <GridContainer alignItems="center" justifyContent="space-between" spacing={1}>
              <GridItem md={10} xs={12}>
                <GridContainer alignItems="center" spacing={1}>
                  <GridItem xs={12} md={3}>
                    <Select label="Año" options={yearsOptions} value={yearValue} onChange={handleChangeYear} />
                  </GridItem>
                  <GridItem xs={12} md={5}>
                    <Select
                      label="Región"
                      loading={loading}
                      options={regionOptions}
                      value={regionValues}
                      onChange={handleChangeRegion}
                    />
                  </GridItem>
                </GridContainer>
              </GridItem>
              <GridItem md={2} xs={12}>
                <Switch
                  checked={currencyValue}
                  color="primary"
                  labelLeft="CLP"
                  labelRight="USD"
                  onChange={handleChangeCurrency}
                />
              </GridItem>
            </GridContainer>
          }
          initialDisplayedView={DataLayoutViews.TABLE}
          noData={!loading && data?.length === 0}
          noDataText="El organismo comprador no cuenta con información relacionada a sus proveedores durante el año seleccionado."
          ref={dataLayoutRef}
          updatedDate={latestUpdate}
          onCaptureImage={handleCaptureImage}
          onChangeView={handleChangeView}
          onDownloadFile={handleDownloadFile}
        >
          <DataLayoutChild view={DataLayoutViews.TABLE}>
            <Card color="blank" elevation="1" margin="0 0 32px">
              <GridContainer padding="0 0 1px">
                <GridItem xs={12}>
                  <DataTable
                    headers={[
                      { label: 'Nombre proveedor', value: 'provider' },
                      { label: 'RUT proveedor', value: 'rut' },
                      { label: 'Cotizaciones enviadas', value: 'totalQuotations' },
                      { label: 'Monto transado', value: 'amount' },
                      { label: 'N° de órdenes de compra', value: 'quantityOC' }
                    ]}
                    headersCellsProps={[
                      { width: '35%' },
                      { enableSort: true, width: '15%' },
                      { enableSort: true, width: '15%' },
                      {
                        enableSort: true,
                        initialDirection: SORT_DIRECTIONS_DICTIONARY.DESC,
                        initialSort: true,
                        width: '15%'
                      },
                      { enableSort: true, width: '20%' }
                    ]}
                    initialRows={15}
                    loading={loading}
                    records={data}
                    recordsDecorators={[
                      ({ provider, rut }) => <Link to={`/organismos-proveedores/${rut}`}>{provider}</Link>,
                      ({ rut }) => rut,
                      ({ totalQuotations }) => totalQuotations,
                      ({ amount, amountUSD }) =>
                        currencyValue ? `USD ${formatAmount(amountUSD)}` : `$${formatAmount(amount)}`,
                      ({ quantityOC }) => quantityOC
                    ]}
                  />
                </GridItem>
              </GridContainer>
            </Card>
          </DataLayoutChild>
        </DataLayout>
      </Content>
    </Root>
  )
}

export default AgilePurchaseProviders
